import { useCallback, useEffect, useRef } from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { ACTION_TANDEM_ERROR, CATEGORY_PRODUCT } from '../constants/analytics';
import { EditorActions, LockState } from '../editor/editorSlice';
import { postToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import { useBeforeLeaveConfirm } from '../utils/BeforeLeaveConfirm';
import Logger from '../utils/Logger';
import { track } from '../utils/analytics';

// Export for testing
// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const OFFLINE_TIMEOUT = 90000;
// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const WARNING_TIMEOUT = 15000;

interface PostSaveTrackerProps {
  postId: string;
}

const PostSaveTracker = ({ postId }: PostSaveTrackerProps) => {
  const timerTriggeredRef = useRef<'warning' | 'offline' | null>(null);

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const offlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanupTimers = useCallback(() => {
    [warningTimerRef, offlineTimerRef].forEach((timerRef) => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    });
  }, []);

  useEffect(() => cleanupTimers, [cleanupTimers]);

  const lockState = useAppSelector((state) => state.editor.lockState);
  const hasUnsavedChanges = useAppSelector(
    (state) => state.editor.unsaved.length() > 0,
  );
  useBeforeLeaveConfirm({
    skip: !!lockState || !hasUnsavedChanges,
    message: 'Your work has not been fully saved. Leave anyways?',
  });

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (lockState && timerTriggeredRef.current !== 'offline') {
      dispatch(
        UIActions.pushToast(postToasts.updateRejectedByServerError, {
          duration: -1,
        }),
      );
      cleanupTimers();
    }
  }, [lockState, dispatch, cleanupTimers]);

  useEffect(() => {
    cleanupTimers();

    if (hasUnsavedChanges) {
      warningTimerRef.current = setTimeout(() => {
        timerTriggeredRef.current = 'warning';
        dispatch(
          UIActions.pushToast(postToasts.saveWarning, {
            duration: OFFLINE_TIMEOUT - WARNING_TIMEOUT - 200,
          }),
        );

        Logger.warn('Post save tracker warning', { postId });
      }, WARNING_TIMEOUT);

      offlineTimerRef.current = setTimeout(() => {
        timerTriggeredRef.current = 'offline';
        dispatch(
          UIActions.pushToast(postToasts.offlineError, { duration: -1 }),
        );
        dispatch(EditorActions.lock(LockState.Timeout));
        Logger.error('Post took too long to save', { postId });
        track(ACTION_TANDEM_ERROR, {
          category: CATEGORY_PRODUCT,
          label: 'offline',
        });
      }, OFFLINE_TIMEOUT);
    } else if (timerTriggeredRef.current) {
      dispatch(UIActions.clearToasts());
      dispatch(UIActions.pushToast(postToasts.saved, { duration: 2000 }));
      dispatch(EditorActions.tryUnlocking());
      timerTriggeredRef.current = null;
    }
  }, [hasUnsavedChanges, cleanupTimers, dispatch, postId]);

  return null;
};

export default PostSaveTracker;
